import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { B } from '../../constants/brand';
import { Btn, Input, Modal, ModalFooter, Spinner } from '../ui';
import { fmt } from '../../utils/format';

// ---------------------------------------------------------------------------
// ATTACH A HOLDING COMPANY BY HAND — the fallback for the deal the hierarchy
// cannot reach.
//
// Harvus normally derives this: tag the operating location on a capital item
// and the entity it rolls up to attaches itself. That walk starts AT a tagged
// location, so a deal that has tagged none — held entirely through an LLC, or
// simply not mapped yet — gets nothing, reports without its borrowing, and the
// strip that exists to say a half is missing renders nothing at all.
//
// THE PICKER IS RANKED, AND THAT IS THE WHOLE POINT OF IT. A dropdown of every
// Sage code puts the original question straight back on the person least able
// to answer it: which of these carries the debt. The server ranks by the two
// marks Sage leaves on an entity (other locations roll up to it; it carries
// debt and no assets — the `DB` half of the Duncan Brake pair exactly) and
// sends the signals in words, so the row explains itself.
//
// TWO MASTERS, ONE RANKED LIST. Sage records a holding company in whichever of
// the two its books happen to use: Duncan Brake's `DB` is a LOCATION carrying
// debt and no assets, while Boggy Creek, LLC exists only in the ENTITY master
// and has no location at all — its money posts to the code `BOGGY` regardless.
// Both are the same question, so they are one list. Splitting them into two
// pickers would make somebody choose a TABLE before they can choose an answer.
//
// A LOCATION THAT CANNOT BE ATTACHED IS SHOWN WITH ITS REASON, NOT HIDDEN.
// A code missing from the list with no explanation is the picker telling
// somebody their entity does not exist. The reason is the API's own text,
// rendered verbatim — inventing a shorter one here would throw away the part
// that says what to do about it.
// ---------------------------------------------------------------------------
export default function AttachSageEntityModal({ dealId, dealName, onClose, onAttached }) {
  const [q, setQ]             = useState('');
  const [data, setData]       = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [picked, setPicked]   = useState(null);
  const [role, setRole]       = useState('Borrowing entity');
  const [notes, setNotes]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    let live = true;
    setData(null); setLoadErr(null);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`deals/${dealId}/sage-entities/candidates${q ? `?q=${encodeURIComponent(q)}` : ''}`);
        if (live) setData(res);
      } catch (e) {
        if (live) setLoadErr(e.message || 'Could not load Sage locations.');
      }
    }, q ? 250 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [dealId, q]);

  const rows = useMemo(() => (data?.candidates || []), [data]);

  // A candidate carries EITHER a locationID or a sageEntityID, so neither alone
  // can key the list or track the selection — keying on locationID alone gives
  // every entity row the same `undefined` key, and React then renders one row
  // and treats every click as the same row.
  const keyOf = (r) => (r?.sageEntityID ? `ent:${r.sageEntityID}` : `loc:${r?.locationID}`);

  const attach = async () => {
    if (!picked) return;
    setSaving(true); setErr(null);
    try {
      const res = await api.post(`deals/${dealId}/sage-entities`, {
        // WHICH FIELD IS WHICH MASTER. The API refuses both at once rather than
        // picking one, so the row's own kind decides and nothing is inferred
        // from a truthy id.
        ...(picked.kind === 'entity'
          ? { sageEntityId: picked.sageEntityID }
          : { locationId: picked.locationID }),
        role, notes,
      });
      onAttached?.(res);
      onClose();
    } catch (e) {
      // Verbatim: the API names the deal already holding it, or the capital
      // item already counting it, which is the actionable half.
      setErr(e.message || 'Could not attach that entity.');
      setSaving(false);
    }
  };

  return (
    <Modal title={`Attach a Sage entity — ${dealName || 'deal'}`} onClose={saving ? () => {} : onClose} width={720}>
      <div style={{ fontSize: 12, color: B.textMd, marginBottom: 12, lineHeight: 1.5 }}>
        An entity carries this deal's <strong>borrowing and equity</strong>; the operating
        location carries the asset. Harvus attaches the entity by itself whenever a capital
        item names an operating location that rolls up to it in Sage — attach one here only
        when it cannot: nothing tagged yet, or no parent recorded in Sage.
        Its postings will count in this deal's Total Cost and actual IRR.
        This searches both the Sage <strong>locations</strong> and the Sage{' '}
        <strong>company entities</strong> — a deal held entirely through an LLC often has
        no location at all.
      </div>

      <Input
        label="Search by Sage code or name"
        value={q}
        onChange={setQ}
        placeholder="e.g. DB, Duncan Break, BOGGY"
      />

      {!data && !loadErr && <Spinner label="Reading the Sage masters…" />}
      {loadErr && <div style={{ color: B.red, fontSize: 13, padding: '12px 0' }}>{loadErr}</div>}

      {data && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: B.textLt, marginBottom: 6 }}>
            Ordered by how much each code looks like an entity — anything Sage records as a
            company entity comes first, then locations others roll up to, then locations
            carrying debt but no assets.
            {data.truncated && ' Showing the strongest matches only; search to narrow it.'}
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto', border: `1px solid ${B.border}`, borderRadius: 10 }}>
            {rows.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, color: B.textMd }}>
                Nothing in Sage matches “{q}”. These come from the{' '}
                <strong>Locations</strong> and <strong>Source Entities</strong> imports on the
                Sage Import tab — if the entity is missing from both, run those imports again.
                {data.entityMaster === false
                  && ' (Company entities are not searchable on this database yet — it is behind migration 193.)'}
              </div>
            )}
            {rows.map(r => (
              <CandidateRow
                key={keyOf(r)}
                row={r}
                selected={!!picked && keyOf(picked) === keyOf(r)}
                onPick={() => r.attachable && setPicked(r)}
              />
            ))}
          </div>
        </div>
      )}

      {picked && (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Input label="Role" value={role} onChange={setRole} placeholder="Borrowing entity" />
          <Input
            label="Why (optional)"
            value={notes}
            onChange={setNotes}
            placeholder="e.g. no operating location in Sage — the LLC holds the note directly"
          />
          {/* A hand-made attachment is never withdrawn by the reconciler for
              lack of a hierarchy link, so it is a standing assertion rather
              than a guess. Say so before it is made, not after. */}
          <div style={{ fontSize: 12, color: B.textMd, lineHeight: 1.5 }}>
            Recorded as <strong>added by hand</strong>. Harvus will not remove it when the Sage
            hierarchy disagrees — only you can. It will still be withdrawn from every deal if
            another deal turns out to claim the same entity.
          </div>
        </div>
      )}

      {err && (
        <div style={{
          marginTop: 12, border: `1px solid ${B.red}`, borderRadius: 8,
          background: 'rgba(192,57,43,0.06)', padding: '10px 12px',
          fontSize: 13, color: B.text, lineHeight: 1.5,
        }}>{err}</div>
      )}

      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
        <Btn onClick={attach} disabled={!picked || saving} loading={saving}>
          {picked ? `Attach ${picked.displayName || picked.sageCode}` : 'Attach entity'}
        </Btn>
      </ModalFooter>
    </Modal>
  );
}

function CandidateRow({ row: r, selected, onPick }) {
  // SageDebt arrives as a SIGNED GL balance and the client negates it once —
  // the same rule the strip and the deal header apply. NULL means "no
  // breakout", deliberately not 0.
  const debt = r.sageDebt == null ? null : -Number(r.sageDebt);
  return (
    <div
      onClick={onPick}
      style={{
        padding: '10px 12px',
        borderBottom: `1px solid ${B.border}`,
        background: selected ? 'rgba(0,0,0,0.04)' : 'transparent',
        cursor: r.attachable ? 'pointer' : 'default',
        opacity: r.attachable ? 1 : 0.65,
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: B.text }}>
          {r.displayName || r.sageCode}
        </span>
        <span style={{ fontSize: 11, color: B.textLt, fontFamily: 'monospace' }}>{r.sageCode}</span>
        {/* WHICH MASTER, ON THE ROW. The two are attached through different
            columns and fixed by different imports, so "run the Locations
            import again" is only actionable if the reader can see that this
            row came from the other one. */}
        <span style={{
          fontSize: 10, letterSpacing: 0.3, textTransform: 'uppercase',
          color: B.textLt, border: `1px solid ${B.border}`, borderRadius: 999, padding: '1px 6px',
        }}>{r.kind === 'entity' ? 'Entity' : 'Location'}</span>
        {debt != null && debt !== 0 && (
          <span style={{ fontSize: 12, color: B.textMd }}>Debt {fmt.currency(debt)}</span>
        )}
        {r.sageTotalCost != null && Number(r.sageTotalCost) !== 0 && (
          <span style={{ fontSize: 12, color: B.textMd }}>Total Cost {fmt.currency(r.sageTotalCost)}</span>
        )}
      </div>
      <div style={{ fontSize: 11, color: B.textLt, marginTop: 2 }}>
        {(r.signals || []).join(' · ')}
      </div>
      {!r.attachable && (
        <div style={{ fontSize: 12, color: B.red, marginTop: 4, lineHeight: 1.45 }}>
          {r.blockedReason}
        </div>
      )}
    </div>
  );
}
